/* eslint-disable max-lines */

import type { Type, ValueProvider } from '@angular/core';
import { Inject, Injectable, Injector, LOCALE_ID } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { INITIAL_STATE, Store } from '@ngrx/store';
import { BehaviorSubject, forkJoin, lastValueFrom, type Observable, of, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';

import type { CurrentUser } from '@core/account-types';
import { ANALYTICS_DISABLED } from '@core/analytics-types';
import type { IAppConfig, Providers } from '@core/common-legacy';
import {
  ADMIN,
  APP_CONFIG,
  AUTH_METHOD,
  AUTH_TOKEN,
  COMPANY_ID,
  ENVIRONMENT,
  loadAngularTranslations,
  PATH_WHITELIST,
  provide,
} from '@core/common-legacy';
import { DevFlagsHttpService } from '@core/dev-flags-data';
import { DEV_FLAGS, DevFlagsMap } from '@core/dev-flags-types';
import { FEATURE_SPEC, FeatureSpecHttpClient } from '@core/feature-spec-data';
import type { FeatureSpec } from '@core/feature-spec-types';
import {
  AuthHelpers,
  AuthorisationStrategy,
  AuthService,
  CookieService,
  GenericAuthService,
  HttpApiErrorHandlerService,
  HttpApiService,
  removeQueryParam,
} from '@core/http-legacy';
import { CookieKey, HttpRequestData } from '@core/http-types';
import {
  LANGUAGE_CONFIG,
  type LanguageConfig,
  LOCALE,
  PHRASE_APP_TRANSLATIONS,
  type PhraseAppTranslations,
  phraseAppTranslationsLoader,
  type SupportedTranslations,
} from '@core/i18n-legacy';
import {
  IntegrationsApiService,
  IntegrationsHttpClient,
  IntegrationsHttpRequestData,
} from '@core/integrations-http-legacy';
import {
  ENABLED_INTEGRATION_KINDS,
  INTEGRATIONS_FEATURE_FLAGS,
  IntegrationsCredentials,
} from '@core/integrations-types';
import { tellentReportsTranslations } from '@core/reports-data';
import { resolveMemberOrgGuid } from '@core/tellent-data';
import { provideTellentDevFlags, TellentDevFlagsHttpClient } from '@core/tellent-dev-flags-data';
import { TellentAdminRequestData, TellentHttpApiService } from '@core/tellent-http-legacy';
import { tellentClientAppTranslations } from '@core/tellent-i18n-legacy/locales';
import {
  type TellentAuthTokenResponse,
  type TellentCurrentUser,
  TellentDevFlagsMap,
  type TellentMember,
  type TellentUserResponse,
} from '@core/tellent-types';
import {
  APP_GUID,
  BRAND,
  ORG_GUID,
  TELLENT_INITIAL_CURRENT_USER,
  TELLENT_NOT_AUTHORIZED,
  TELLENT_SUITE_ENABLED,
  TellentBrand,
} from '@core/tellent-types';
import { setDefaultTimezone } from '@core/time-legacy';
import { rtiTranslations } from '@core/translations-integrations-logic';
import { appTranslations } from '@core/translations-webapp-legacy';
import { uiTranslations } from '@core/user-interface-legacy/locales';
import { decodeHTML } from '@core/utils-legacy';

import type { RecruiteeAuthStrategy } from '../recruitee-auth-strategy';
import { IntegrationsRequestData } from './app/config/integrations-request-data';
import { RecruiteeRequestData } from './app/config/recruitee-request-data';
import { WebappHttpApiErrorHandlerService } from './app/main/http-api-error-handler.service';
import type { AppEnvironment } from './environments/interface';
import { wysiwygTranslations } from './locales/wysiwyg';

const hotModule = (<any>import.meta).webpackHot;

interface FeaturesData {
  devFlags: DevFlagsMap;
  featureSpec: FeatureSpec[];
  tellentDevFlags: TellentDevFlagsMap;
}

interface HMRCache {
  store?: any;
  authToken?: string;
  preloaded?: { admin: CurrentUser; companyId: number | null; locale: string };
  preloadedNext?: [
    phraseAppTranslations: PhraseAppTranslations[],
    featuresData: FeaturesData,
    ...temp: any[],
  ];
}

const NO_COMPANY_PATH_WHITELIST = [
  '/admin',
  '/secure_change/token',
  '/secure_change/email_code',
  // create company
  '/companies',
  // delete account
  '/deletion_requests',
];

@Injectable()
export class WebAppBootstrap {
  private readonly availableLocales: string[] = this.languageConfig.languages;
  private readonly defaultLocale: string = this.languageConfig.defaultLanguage;

  private authMethod = 'Bearer';
  private currentInjector = new BehaviorSubject(this.initialInjector);
  private forwardProviders: Providers[] = [];
  private isDOMContentLoaded = new Promise(resolve =>
    document.addEventListener('DOMContentLoaded', resolve, false),
  );
  private onDestroy$ = new Subject<void>();
  private hmrCache: HMRCache = hotModule?.data || {};

  private startupZone = Zone.current.fork({
    name: 'bootstrap startup requests',
    properties: {
      startup: true,
    },
  });

  constructor(
    private authService: AuthService,
    private authHelpers: AuthHelpers,
    private cookieService: CookieService,
    @Inject(LANGUAGE_CONFIG) private languageConfig: LanguageConfig,
    @Inject(AuthorisationStrategy) private authStrategy: RecruiteeAuthStrategy,
    private initialInjector: Injector,
    @Inject(APP_CONFIG) private appConfig: IAppConfig,
    private genericAuthService: GenericAuthService<TellentAuthTokenResponse, TellentUserResponse>,
  ) {
    if (hotModule) {
      this.handleHmr();
    }
  }

  public async bootstrap(forwardProviders: Providers[], rootModule: Promise<Type<any>>) {
    this.forwardProviders = [...this.forwardProviders, ...forwardProviders];
    await this.beforeAuth();
    const { value: injector } = this.currentInjector;
    const authToken =
      injector.get(AUTH_TOKEN, null, { optional: true }) ??
      (await this.getFromHmrCache(
        'authToken',
        async () => (await this.startupZone.run(() => this.authorize()))!,
      ));
    if (!authToken) return;

    const {
      translations,
      integrationsFeatureFlags,
      enabledIntegrationKinds,
      featuresData: { devFlags, featureSpec, tellentDevFlags },
    }: Awaited<ReturnType<WebAppBootstrap['preloadData']>> = await this.startupZone.run(() =>
      this.preloadData(authToken),
    );
    const ngRootModule = await rootModule;

    const providers = [
      ...this.getProviders(),
      provide(PHRASE_APP_TRANSLATIONS, { useValue: translations }),
      provide(DEV_FLAGS, { useValue: devFlags }),
      provideTellentDevFlags({ useValue: tellentDevFlags }),
      provide(INTEGRATIONS_FEATURE_FLAGS, { useValue: integrationsFeatureFlags }),
      provide(ENABLED_INTEGRATION_KINDS, { useValue: enabledIntegrationKinds }),
      provide(FEATURE_SPEC, { useValue: featureSpec }),
      ...(this.hmrCache.store ? [provide(INITIAL_STATE, { useValue: this.hmrCache.store })] : []),
    ];
    const rootModuleRef = await platformBrowserDynamic(providers).bootstrapModule(ngRootModule, {
      ngZone: 'noop',
    });
    const store = rootModuleRef.injector.get(Store);
    store.pipe(takeUntil(this.onDestroy$)).subscribe(value => (this.hmrCache.store = value));
  }

  private beforeAuth() {
    return Promise.all([
      document.readyState === 'complete' ? Promise.resolve() : this.isDOMContentLoaded,
    ]);
  }

  private async authorize(): Promise<string | null> {
    const authToken = await this.authService
      .getAccessToken()
      .catch(err => console.error(`~~~~~~ getAccessToken ERROR ~~~~~~\n`, err));
    if (!authToken) {
      this.authStrategy.onAuthorizationFailed();
      return null;
    }
    this.authService.saveAccessTokenInCookie(authToken, this.appConfig.hasHttps);
    return authToken;
  }

  // eslint-disable-next-line max-lines-per-function
  private async preloadData(authToken: string) {
    const environment = <AppEnvironment>this.currentInjector.value.get(ENVIRONMENT);

    let tellentCurrentUserWithCurrentOrg = null;
    let isTellentAndNotAuthorizedByTellent = null;

    const { admin, companyId, locale } = await this.getFromHmrCache('preloaded', () =>
      this.authService
        .fetchAdmin(
          authToken,
          this.appConfig.apiUrl,
          this.authMethod,
          RecruiteeRequestData.getIdentityHeaders(environment, this.appConfig.isE2e),
        )
        .then((data: { admin: CurrentUser }) => {
          const admin = {
            ...data.admin,
            companies: this.decodeCompanyNames(data.admin.companies),
          };
          const { companies, companyId } = admin;
          const currentCompany = companies.find(company => company.id === companyId) || null;
          const locale = this.getLocale(admin, currentCompany);

          setDefaultTimezone(admin.timezone);

          this.authHelpers.clearCompanyCookies();
          if (companyId) {
            this.authHelpers.saveCompanyInCookie(companyId.toString());
          }
          removeQueryParam('authorization_code');
          removeQueryParam('company_id');
          removeQueryParam('company');
          return { companyId, locale, admin };
        })
        .catch(error => {
          this.authService.onResponseError(error);
          return Promise.reject(error);
        }),
    );

    const { tsEnabled, tsAppGuid } = this.resolveCurrentCompany(admin, companyId!);

    /*
     Until all users are migrated and logged on with new tellent auth,
     there might be a situation where the endpoint responds with an error due to an invalid token.

     To be removed after one login project release.
   */

    if (tsEnabled) {
      try {
        const tellentUserResponse = await this.genericAuthService.fetchUser(
          authToken,
          environment.tellentApiUrl + '/api/me',
        );
        const tellentCurrentUser = tellentUserResponse.user;
        const cookieOrgGuid = this.cookieService.get(CookieKey.OrgGuid);

        const orgGuid = !cookieOrgGuid
          ? !!tellentCurrentUser && tsAppGuid
            ? this.resolveOrgGuid(tsAppGuid!, tellentCurrentUser!)
            : null
          : cookieOrgGuid;

        tellentCurrentUserWithCurrentOrg = {
          ...tellentCurrentUser,
          currentOrgGuid: orgGuid,
        };
      } catch {
        if (tsAppGuid) isTellentAndNotAuthorizedByTellent = true;
      }
    }

    this.updateInjector([
      provide(AUTH_TOKEN, { useValue: authToken }),
      provide(COMPANY_ID, { useValue: companyId }),
      provide(AUTH_METHOD, { useValue: this.authMethod }),
      provide(LOCALE, { useValue: locale }),
      provide(LOCALE_ID, { useValue: locale }),
      provide(ADMIN, { useValue: admin }),
      provide(ORG_GUID, { useValue: tellentCurrentUserWithCurrentOrg?.currentOrgGuid || null }),
      provide(APP_GUID, { useValue: tsAppGuid }),
      provide(BRAND, { useValue: TellentBrand.Recruitee }),
      provide(TELLENT_SUITE_ENABLED, {
        useValue: tsEnabled && !isTellentAndNotAuthorizedByTellent,
      }),
      provide(TELLENT_NOT_AUTHORIZED, { useValue: !!isTellentAndNotAuthorizedByTellent }),
      provide(TELLENT_INITIAL_CURRENT_USER, {
        useValue: <any>tellentCurrentUserWithCurrentOrg || {},
      }),
      provide(ANALYTICS_DISABLED, { useValue: this.authService.isSaSoftLogin }),
      provide(DevFlagsHttpService, { useClass: DevFlagsHttpService }),
      provide(HttpApiService, { useClass: HttpApiService }),
      provide(WebappHttpApiErrorHandlerService, { useClass: WebappHttpApiErrorHandlerService }),
      provide(HttpRequestData, { useClass: RecruiteeRequestData }),
      provide(IntegrationsHttpRequestData, { useClass: IntegrationsRequestData }),
      provide(HttpApiErrorHandlerService, { useExisting: WebappHttpApiErrorHandlerService }),
      provide(PATH_WHITELIST, { useValue: companyId ? null : NO_COMPANY_PATH_WHITELIST }),
      provide(IntegrationsApiService, { useClass: IntegrationsApiService }),
      provide(IntegrationsCredentials, { useClass: IntegrationsCredentials }),
      provide(IntegrationsHttpClient, { useClass: IntegrationsHttpClient }),
      provide(FeatureSpecHttpClient, { useClass: FeatureSpecHttpClient }),
      provide(TellentAdminRequestData, { useClass: TellentAdminRequestData }),
      provide(TellentHttpApiService, { useClass: TellentHttpApiService }),
      provide(TellentDevFlagsHttpClient, { useClass: TellentDevFlagsHttpClient }),
    ]);

    const integrationsHttpClient = this.currentInjector.value.get(IntegrationsHttpClient);
    const features = lastValueFrom(
      this.loadFeatures(companyId, tsEnabled && !isTellentAndNotAuthorizedByTellent),
    );

    const integrationsFeatureFlagsData = companyId
      ? lastValueFrom(integrationsHttpClient.loadFeatureFlags())
      : Promise.resolve().then(() => ({}));
    const enabledIntegrationKindsData = companyId
      ? lastValueFrom(integrationsHttpClient.loadEnabledIntegrationKinds())
      : Promise.resolve().then(() => []);

    const [translations, featuresData, integrationsFeatureFlags, enabledIntegrationKinds] =
      await this.getFromHmrCache('preloadedNext', () =>
        Promise.all([
          this.loadTranslations(<SupportedTranslations>locale),
          features,
          integrationsFeatureFlagsData.catch(() => ({})),
          enabledIntegrationKindsData.catch(() => []),
          loadAngularTranslations(locale).catch(err => console.error(err)),
        ]),
      );

    return { translations, integrationsFeatureFlags, enabledIntegrationKinds, featuresData };
  }

  private loadFeatures(companyId: number | null, tsEnabled: boolean): Observable<FeaturesData> {
    const devFlagsHttpService = this.currentInjector.value.get(DevFlagsHttpService);
    const featureSpecHttpClient = this.currentInjector.value.get(FeatureSpecHttpClient);
    const tellentDevFlagsHttpClient = this.currentInjector.value.get(TellentDevFlagsHttpClient);

    const empty: FeaturesData = {
      devFlags: <DevFlagsMap>{},
      featureSpec: <FeatureSpec[]>[],
      tellentDevFlags: <TellentDevFlagsMap>{},
    };

    if (!companyId) {
      return of(empty);
    }

    return forkJoin({
      devFlags: devFlagsHttpService.fetch(),
      featureSpec: featureSpecHttpClient.fetch().pipe(
        map(response => response.features),
        catchError(error => {
          console.error(error);
          return of(<FeatureSpec[]>[]);
        }),
      ),
      tellentDevFlags: tsEnabled ? tellentDevFlagsHttpClient.fetch() : of(<TellentDevFlagsMap>{}),
    }).pipe(
      take(1),
      catchError(error => {
        console.error(error);
        return of(empty);
      }),
    );
  }

  private loadTranslations(locale: SupportedTranslations) {
    return phraseAppTranslationsLoader(locale, 'en', [
      appTranslations,
      uiTranslations,
      rtiTranslations,
      wysiwygTranslations,
      tellentClientAppTranslations,
      tellentReportsTranslations,
    ]);
  }

  private getProviders(): ValueProvider[] {
    const { value: injector } = this.currentInjector;
    return this.forwardProviders.reduce(
      (acc: ValueProvider[], { provide }) => [...acc, { provide, useValue: injector.get(provide) }],
      [],
    );
  }

  private updateInjector(providers: Providers[]) {
    this.forwardProviders = [...this.forwardProviders, ...providers];
    const { value: injector } = this.currentInjector;
    this.currentInjector.next(Injector.create({ providers, parent: injector }));
  }

  private handleHmr() {
    const statusHandler = (status: string) => {
      if (status === 'check') {
        hotModule.invalidate();
        hotModule.removeStatusHandler(statusHandler);
      }
    };
    hotModule.status(statusHandler);
    hotModule.dispose((data: any) => {
      if (!data) return;

      this.onDestroy$.next();
      Object.assign(data, this.hmrCache);
    });
  }

  private async getFromHmrCache<
    K extends keyof HMRCache,
    T extends () => Promise<Exclude<HMRCache[K], null | undefined>>,
  >(key: K, fn: T): Promise<ReturnType<T>> {
    if (this.hmrCache[key]) return this.hmrCache[key];

    const value = await fn();
    this.hmrCache = {
      ...this.hmrCache,
      [key]: value,
    };
    return value;
  }

  private resolveOrgGuid(appGuid: string, user: TellentCurrentUser): TellentMember['orgGuid'] {
    return resolveMemberOrgGuid(user, appGuid);
  }

  private resolveCurrentCompany(admin: CurrentUser, companyId: number) {
    return admin.companies.find(c => c.id === companyId) || { tsEnabled: false, tsAppGuid: null };
  }

  private getLocale(
    admin: { preferredLanguage: string },
    company: { language: string } | null,
  ): string {
    if (
      admin &&
      admin.preferredLanguage &&
      admin.preferredLanguage !== 'auto' &&
      this.availableLocales.includes(admin.preferredLanguage)
    ) {
      return admin.preferredLanguage;
    } else if (company && company.language && this.availableLocales.includes(company.language)) {
      return company.language;
    }

    return this.defaultLocale;
  }

  private getLocaleFromBrowser(): string {
    let localeFromBrowser;

    if (navigator.languages && navigator.languages.length) {
      localeFromBrowser = navigator.languages.find(l =>
        this.availableLocales.includes(l.substring(0, 2)),
      );
    }

    if (localeFromBrowser) {
      return localeFromBrowser.substring(0, 2);
    }

    return this.defaultLocale;
  }

  private decodeCompanyNames(companies: CurrentUser['companies']): CurrentUser['companies'] {
    return companies.map(company => ({
      ...company,
      name: decodeHTML(company.name),
    }));
  }
}
